import React from 'react';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { cn } from '@/lib/utils';

interface FooterProps {
  className?: string;
}

const Footer: React.FC<FooterProps> = ({ className }) => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    {
      name: 'GitHub',
      href: 'https://github.com/jermarino16',
      icon: (
        <svg className='h-5 w-5' fill='currentColor' viewBox='0 0 24 24'>
          <path d='M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z' />
        </svg>
      ),
    },
    {
      name: 'Email',
      href: 'mailto:jermarino16@gmail.com',
      icon: (
        <svg
          className='h-5 w-5'
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
          />
        </svg>
      ),
    },
  ];

  const quickLinks = [
    { name: 'About', href: '#about' },
    { name: 'Skills', href: '#skills' },
    { name: 'Experience', href: '#experience' },
    { name: 'Education', href: '#education' },
    { name: 'Projects', href: '#projects' },
    { name: 'Contact', href: '#contact' },
  ];

  return (
    <footer className={cn('border-t bg-background', className)}>
      <div className='container mx-auto px-4 py-8'>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
          {/* Contact Info */}
          <div className='space-y-4'>
            <h3 className='text-lg font-semibold text-foreground'>
              Get In Touch
            </h3>
            <p className='text-sm text-muted-foreground'>
              Senior Software Engineer passionate about building scalable
              solutions and leading high-performing teams.
            </p>
            <div className='flex space-x-4'>
              {socialLinks.map(link => (
                <Button
                  key={link.name}
                  variant='ghost'
                  size='sm'
                  className='h-9 w-9 p-0'
                  asChild
                >
                  <a
                    href={link.href}
                    target={link.name === 'Email' ? undefined : '_blank'}
                    rel={
                      link.name === 'Email' ? undefined : 'noopener noreferrer'
                    }
                    aria-label={link.name}
                  >
                    {link.icon}
                  </a>
                </Button>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className='space-y-4'>
            <h3 className='text-lg font-semibold text-foreground'>
              Quick Links
            </h3>
            <nav className='flex flex-col space-y-2'>
              {quickLinks.map(link => (
                <a
                  key={link.name}
                  href={link.href}
                  className='text-sm text-muted-foreground hover:text-foreground transition-colors'
                >
                  {link.name}
                </a>
              ))}
            </nav>
          </div>

          {/* Professional Info */}
          <div className='space-y-4'>
            <h3 className='text-lg font-semibold text-foreground'>
              Professional
            </h3>
            <div className='space-y-2 text-sm text-muted-foreground'>
              <p>Available for new opportunities</p>
              <p>Remote & On-site work</p>
              <p>Full-time & Contract</p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className='mt-8 pt-8 border-t flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0'>
          <p className='text-sm text-muted-foreground'>
            © {currentYear} Jeremy Marino. All rights reserved.
          </p>
          <div className='flex items-center gap-4'>
            <p className='text-sm text-muted-foreground'>
              Built with React, TypeScript & Tailwind CSS
            </p>
            <ThemeToggle size='sm' />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
